/**
 * Typed API client. Base URL comes from env; all request/response shapes
 * must match contracts/api/v1/* — do not invent field names.
 *
 * Default is same-origin `/api/v1` so Coolify/production (SPA at
 * https://host/app/) hits the nginx gateway on the same host. Override with
 * VITE_API_BASE_URL only when you intentionally call a remote API.
 * Local `vite` dev uses the proxy in vite.config.ts for `/api`.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

/** localStorage key holding the rep's JWT (set by the auth entry point). */
export const AUTH_TOKEN_KEY = "pg_auth_token";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Parsed JSON error body from the API (e.g. 422 missing_fields / geofence_state). */
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...authHeaders(),
    ...(init?.headers as Record<string, string> | undefined),
  };
  // Multipart uploads: the browser must set Content-Type with its own boundary.
  if (init?.body instanceof FormData) delete headers["Content-Type"];
  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    // Token expired/invalid: any 401 outside the login call kills the session
    // and bounces to the login screen (hard navigation resets all app state).
    // Guarded against loops when already on /login.
    if (res.status === 401 && path !== "/auth/login") {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem("pg_auth_user"); // AUTH_USER_KEY (domains/auth/session)
      const loginUrl = `${import.meta.env.BASE_URL}login`;
      if (!window.location.pathname.endsWith("/login")) {
        window.location.assign(loginUrl);
      }
    }
    let details: Record<string, unknown> | undefined;
    let message = `${init?.method ?? "GET"} ${path} failed (${res.status})`;
    try {
      const body = (await res.json()) as Record<string, unknown>;
      details = body;
      if (typeof body.error === "string") message = body.error;
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new ApiError(res.status, message, details);
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown, idempotencyKey?: string) =>
    request<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string) =>
    request<T>(path, { method: "DELETE" }),
  /**
   * Multipart upload (TASK-007b). Content-Type is intentionally NOT set —
   * the browser must generate the multipart boundary itself.
   */
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: "POST", body: form }),
  /** Authenticated binary download (Excel templates, error exports). */
  download: async (path: string, filename: string) => {
    const res = await fetch(`${BASE_URL}${path}`, { headers: authHeaders() });
    if (!res.ok) {
      let message = `GET ${path} failed (${res.status})`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) message = body.error;
      } catch { /* ignore */ }
      throw new ApiError(res.status, message);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

/** Absolute URL for server-relative file paths (e.g. "/uploads/<file>"). */
export function fileUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  const origin = /^https?:\/\//.test(BASE_URL)
    ? new URL(BASE_URL).origin
    : window.location.origin;
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}
